import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { fetchEscalations, resolveEscalation } from '@/api/admin'
import { MessageSquare, Check, Clock, ExternalLink } from 'lucide-react'
import { formatDateTime } from '@/lib/utils'
import { toast } from 'sonner'

export function EscalationsView() {
  const queryClient = useQueryClient()
  
  const { data, isLoading } = useQuery({
    queryKey: ['admin-escalations'],
    queryFn: fetchEscalations,
  })

  const resolveMutation = useMutation({
    mutationFn: resolveEscalation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-escalations'] })
      toast.success('Escalation marked as resolved')
    },
  })

  if (isLoading) {
    return <div className="h-48 animate-pulse rounded-lg bg-muted" />
  }

  const escalations = data?.escalations || []
  const unresolved = escalations.filter((e: any) => !e.resolved)
  const resolved = escalations.filter((e: any) => e.resolved)

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        {/* Unresolved */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-yellow-500" />
              Pending ({unresolved.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {unresolved.map((escalation: any) => (
              <EscalationCard 
                key={escalation.id} 
                escalation={escalation}
                onResolve={() => resolveMutation.mutate(escalation.id)}
                isResolving={resolveMutation.isPending}
              />
            ))}
            {unresolved.length === 0 && (
              <p className="text-muted-foreground text-center py-8">
                No pending escalations
              </p>
            )}
          </CardContent>
        </Card>

        {/* Resolved */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Check className="h-5 w-5 text-green-500" />
              Resolved ({resolved.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {resolved.map((escalation: any) => (
              <EscalationCard 
                key={escalation.id} 
                escalation={escalation}
              />
            ))}
            {resolved.length === 0 && (
              <p className="text-muted-foreground text-center py-8">
                No resolved escalations yet
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function EscalationCard({ 
  escalation, 
  onResolve,
  isResolving 
}: { 
  escalation: any
  onResolve?: () => void
  isResolving?: boolean
}) {
  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
            <MessageSquare className="h-5 w-5" />
          </div>
          <div>
            <p className="font-medium">{escalation.partner_email}</p>
            <p className="text-sm text-muted-foreground">
              {formatDateTime(escalation.created_at)}
            </p>
          </div>
        </div>
        <Badge variant={escalation.resolved ? 'secondary' : 'default'}>
          {escalation.resolved ? 'Resolved' : 'Pending'}
        </Badge>
      </div>
      
      <p className="mt-3 text-sm">{escalation.question}</p>
      
      <div className="mt-3 flex items-center gap-2">
        <Badge variant="outline">Section: {escalation.section}</Badge>
      </div>
      
      {onResolve && (
        <Button 
          size="sm" 
          className="mt-3 w-full"
          onClick={onResolve}
          disabled={isResolving}
        >
          <Check className="mr-2 h-4 w-4" />
          Mark as Resolved
        </Button>
      )}
    </div>
  )
}
